import React, { useMemo, useState } from 'react';
import { Button, Alert } from 'antd';
import { useSelector } from 'utils/use-selector';
import { getCurrentGuide } from 'store/guide-editor/selectors';
import Loading from 'components/Loading';
import { isCollectionCore, isCollectionTheme } from 'utils/collection-utils';
import {
    refreshTopCollectionsForGuide,
    refreshCollectionsForGuide,
    useTopCollections,
    useCollections,
} from 'api/collections.api';
import CollectionsSet from './CollectionsSet';
import TopCollectionsSet from './TopCollectionsSet';
import SpotFilterSelector from './SpotFilterSelector';
import './style.css';

function GuideEditorCollections() {
    const currentGuide = useSelector(getCurrentGuide);
    const guideId = useMemo(() => currentGuide?.id, [currentGuide]);

    /** @type {[Source[], React.Dispatch<Source[]>]} */
    const [filterBySources, setFilterBySources] = useState(null);
    const {
        collections,
        isLoading: isValidatingCollections,
        error: errorLoadingCollections,
    } = useCollections(guideId);

    const {
        topCollections,
        isLoading: isValidatingTopCollections,
        error: errorLoadingTopCollections,
    } = useTopCollections(guideId);

    const coreCollections = useMemo(
        () => (collections || []).filter(isCollectionCore),
        [collections],
    );
    const themeCollections = useMemo(
        () => (collections || []).filter(isCollectionTheme),
        [collections],
    );

    const isLoadingCollections = !collections && isValidatingCollections;
    const isLoadingTopCollections =
        !topCollections && isValidatingTopCollections;

    return (
        <>
            {!isLoadingCollections && (
                <>
                    <SpotFilterSelector
                        sources={filterBySources || []}
                        onChangeSources={(sources) =>
                            setFilterBySources(sources)
                        }
                    />

                    <CollectionsSet
                        collections={coreCollections}
                        title="Core collections"
                        filterBySources={filterBySources}
                    />

                    <CollectionsSet
                        collections={themeCollections}
                        title="Theme collections"
                        filterBySources={filterBySources}
                    />
                </>
            )}

            {!isLoadingCollections &&
                !isLoadingTopCollections &&
                !errorLoadingTopCollections &&
                !!topCollections && (
                    <TopCollectionsSet
                        collections={topCollections}
                        title="Top collections"
                        filterBySources={filterBySources}
                    />
                )}

            {isLoadingCollections && (
                <div className="full-page-tabs-loading">
                    <Loading />
                </div>
            )}

            {errorLoadingCollections && (
                <div className="full-page-tabs-error">
                    <Alert
                        className="grow-full-flex mb-l"
                        message="There was an error loading collections"
                        description={errorLoadingCollections.toString()}
                        type="error"
                        showIcon
                        action={
                            <Button
                                size="large"
                                onClick={() => {
                                    refreshCollectionsForGuide(guideId);
                                }}
                            >
                                Try again
                            </Button>
                        }
                    />
                </div>
            )}
            {errorLoadingTopCollections && (
                <div className="full-page-tabs-error">
                    <Alert
                        className="grow-full-flex mb-l"
                        message="There was an error loading top collections"
                        description={errorLoadingTopCollections.toString()}
                        type="error"
                        showIcon
                        action={
                            <Button
                                size="large"
                                onClick={() => {
                                    refreshTopCollectionsForGuide(guideId);
                                }}
                            >
                                Try again
                            </Button>
                        }
                    />
                </div>
            )}
        </>
    );
}

export default GuideEditorCollections;
